/**
 * File type detection utilities for document viewer routing.
 *
 * Determines which viewport component to use based on file MIME type.
 * Used by DocumentViewer to route files to PdfViewport.
 * DOCX files are converted to PDF server-side before preview.
 */

/**
 * Supported file view types for the document viewer.
 * Each type corresponds to a specific viewport component.
 */
export enum FileViewType {
  PDF = 'pdf',
  DOCX = 'docx',
  IMAGE = 'image',
  MARKDOWN = 'markdown',
  UNKNOWN = 'unknown',
}

/**
 * Mapping of MIME types to their corresponding view types.
 * Uses MIME type strings as keys (intentionally not camelCase).
 */
const MIME_TO_VIEW_TYPE: Record<string, FileViewType> = {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  'application/pdf': FileViewType.PDF,
  // eslint-disable-next-line @typescript-eslint/naming-convention
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
    FileViewType.DOCX,
  // eslint-disable-next-line @typescript-eslint/naming-convention
  'image/png': FileViewType.IMAGE,
  // eslint-disable-next-line @typescript-eslint/naming-convention
  'image/jpeg': FileViewType.IMAGE,
  // eslint-disable-next-line @typescript-eslint/naming-convention
  'image/jpg': FileViewType.IMAGE,
  // eslint-disable-next-line @typescript-eslint/naming-convention
  'image/gif': FileViewType.IMAGE,
  // eslint-disable-next-line @typescript-eslint/naming-convention
  'image/webp': FileViewType.IMAGE,
  // eslint-disable-next-line @typescript-eslint/naming-convention
  'image/svg+xml': FileViewType.IMAGE,
  // eslint-disable-next-line @typescript-eslint/naming-convention
  'text/markdown': FileViewType.MARKDOWN,
  // eslint-disable-next-line @typescript-eslint/naming-convention
  'text/x-markdown': FileViewType.MARKDOWN,
};

/**
 * MIME types accepted for document upload.
 * Used by FileUploadControls for the file input accept attribute.
 */
export const ACCEPTED_DOCUMENT_MIME_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/markdown',
  '.md',
  'application/zip',
  '.zip',
].join(',');

/**
 * Detect file view type from a File object, Blob, or MIME type string.
 *
 * Priority:
 * 1. File/Blob.type property (most reliable)
 * 2. MIME type string
 * 3. Filename extension (for File objects)
 * 4. Falls back to UNKNOWN
 *
 * @param source - File object, Blob, or MIME type string
 * @param filename - Optional filename for extension-based detection fallback
 * @returns The detected FileViewType
 *
 * @example
 * // From File object
 * const file = new File(['content'], 'doc.docx', { type: 'application/vnd...' });
 * getFileViewType(file); // FileViewType.DOCX
 *
 * @example
 * // From Blob
 * const blob = new Blob(['content'], { type: 'application/pdf' });
 * getFileViewType(blob); // FileViewType.PDF
 *
 * @example
 * // From MIME type string
 * getFileViewType('application/pdf'); // FileViewType.PDF
 *
 * @example
 * // From filename when type is missing
 * const file = new File(['content'], 'doc.docx', { type: '' });
 * getFileViewType(file, 'doc.docx'); // FileViewType.DOCX
 */
export function getFileViewType(
  source: File | Blob | string | null | undefined,
  filename?: string
): FileViewType {
  if (!source) {
    return FileViewType.UNKNOWN;
  }

  // Handle File or Blob - use type property
  if (source instanceof File || source instanceof Blob) {
    const mimeType = source.type?.toLowerCase();

    // Try MIME type first
    if (mimeType && MIME_TO_VIEW_TYPE[mimeType]) {
      return MIME_TO_VIEW_TYPE[mimeType];
    }

    // Fallback to filename extension for File objects
    if (source instanceof File && source.name) {
      const typeFromExtension = getFileViewTypeFromFilename(source.name);
      if (typeFromExtension !== FileViewType.UNKNOWN) {
        return typeFromExtension;
      }
    }

    // Fallback to provided filename parameter
    if (filename) {
      const typeFromExtension = getFileViewTypeFromFilename(filename);
      if (typeFromExtension !== FileViewType.UNKNOWN) {
        return typeFromExtension;
      }
    }

    return FileViewType.UNKNOWN;
  }

  // Handle MIME type string directly (not a blob URL)
  if (typeof source === 'string' && !source.startsWith('blob:')) {
    const typeFromMime = MIME_TO_VIEW_TYPE[source.toLowerCase()];
    if (typeFromMime) {
      return typeFromMime;
    }

    if (filename) {
      return getFileViewTypeFromFilename(filename);
    }

    return FileViewType.UNKNOWN;
  }

  // Blob URLs don't contain type info - caller should provide blob instead
  return FileViewType.UNKNOWN;
}

/**
 * Detect file view type from filename extension.
 *
 * @param filename - Filename with extension
 * @returns The detected FileViewType
 */
function getFileViewTypeFromFilename(filename: string): FileViewType {
  const extension = filename.toLowerCase().split('.').pop();

  switch (extension) {
    case 'pdf': {
      return FileViewType.PDF;
    }

    case 'docx': {
      return FileViewType.DOCX;
    }

    case 'md':
    case 'markdown': {
      return FileViewType.MARKDOWN;
    }

    case 'png':
    case 'jpg':
    case 'jpeg':
    case 'gif':
    case 'webp': {
      return FileViewType.IMAGE;
    }

    case undefined:
    default: {
      return FileViewType.UNKNOWN;
    }
  }
}

/**
 * Check if a file type is viewable in the document viewer.
 *
 * @param source - File object, Blob, or MIME type string
 * @returns True if the file can be displayed in the viewer
 */
export function isViewableFileType(
  source: File | Blob | string | null | undefined
): boolean {
  const viewType = getFileViewType(source);
  return viewType !== FileViewType.UNKNOWN;
}

/**
 * Get a human-readable label for a file view type.
 *
 * @param viewType - The FileViewType to get a label for
 * @returns Human-readable label
 */
export function getFileViewTypeLabel(viewType: FileViewType): string {
  switch (viewType) {
    case FileViewType.PDF: {
      return 'PDF Document';
    }

    case FileViewType.DOCX: {
      return 'Word Document';
    }

    case FileViewType.IMAGE: {
      return 'Image';
    }

    case FileViewType.MARKDOWN: {
      return 'Markdown';
    }

    case FileViewType.UNKNOWN: {
      return 'Unknown Format';
    }
  }
}
